import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Helmet } from "react-helmet-async"
import Modal from "react-responsive-modal"
import BreadCrumbs from "../components/BreadCrumbs"
import CommunityActivityColumn from "../components/CommunityActivityColumn"
import ConvoyTripCard from "../components/ConvoyTripCard"
import CommunitySidebar from "../components/CommunitySidebar"
import { ToastNoPlace } from "../components/ToastNotification"
import VicinityContext from "../context/vicinityContext"
import {
    getConvoyTrips,
    leaveCheckerTrip,
    pingCheckerTrip,
    respondCheckerCompanion,
} from "../services/checkerService"
import { useCommunityManager } from "../hooks/useCommunityManager"
import "../css/Checker.css"

// The same GCS-hosted illustration the Android app shows in place of an
// empty convoy list (see CONVOY_EMPTY_STATE_IMAGE_URL in its ConvoyScreen.kt).
const CONVOY_EMPTY_STATE_IMAGE_URL =
    "https://storage.googleapis.com/chaperon/media/images/Galina4.jpg"

// Regular polling fallback: unlike the owner's own Checker page, there's no
// server-side push for an ordinary "safe" check-in a companion is watching
// for, so this is what actually keeps missed_checkins_count/safety_status
// current for someone watching an active trip. Matches the Android app.
const CONVOY_POLL_INTERVAL_MS = 30_000

function toArray(result) {
    if (Array.isArray(result)) return result
    if (Array.isArray(result?.data)) return result.data
    return []
}

export default function Convoy() {
    const communityManager = useCommunityManager()
    const { setToastMessage, setShowToast } = useContext(VicinityContext)

    const [trips, setTrips] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState("")
    const [respondingTripId, setRespondingTripId] = useState(null)
    // Trip awaiting confirmation on "Stop watching" -- a safety-monitoring
    // commitment shouldn't disappear on a single mis-tap.
    const [leavingTrip, setLeavingTrip] = useState(null)
    const [isLeaving, setIsLeaving] = useState(false)

    const crumbs = useMemo(
        () => [
            { path: "/community", name: "Community" },
            { path: "/community/convoy", name: "Convoy" },
        ],
        [],
    )

    const showToast = useCallback(
        (message) => {
            setToastMessage(message)
            setShowToast(true)
        },
        [setToastMessage, setShowToast],
    )

    // silent=true skips the error banner -- used by the background-triggered
    // refreshes below (poll, tab regaining visibility) so a transient
    // failure doesn't nag the user; the very first load still surfaces it.
    const fetchTrips = useCallback(async (silent) => {
        setIsLoading((prev) => (silent ? prev : true))
        try {
            const result = await getConvoyTrips({ limit: 100 })
            setTrips(toArray(result))
            setError("")
        } catch (requestError) {
            if (!silent) {
                setError(requestError?.message || "Error fetching convoy trips")
            }
            // Deliberately not clearing the existing list on a transient
            // failure -- it'll simply refresh in on the next successful poll.
        } finally {
            setIsLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchTrips(false)
    }, [fetchTrips])

    useEffect(() => {
        const id = setInterval(() => fetchTrips(true), CONVOY_POLL_INTERVAL_MS)
        return () => clearInterval(id)
    }, [fetchTrips])

    // Catches up when the tab is shown again, similar in spirit to the
    // Android app's own resume-triggered refresh.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === "visible") fetchTrips(true)
        }
        document.addEventListener("visibilitychange", onVisible)
        return () => document.removeEventListener("visibilitychange", onVisible)
    }, [fetchTrips])

    const handleAccept = async (trip) => {
        setRespondingTripId(trip.id)
        try {
            await respondCheckerCompanion(trip.id, "accepted")
        } catch (requestError) {
            showToast(requestError?.message || "Couldn't accept that invite")
        } finally {
            setRespondingTripId(null)
            await fetchTrips(true)
        }
    }

    const handleDecline = async (trip) => {
        setRespondingTripId(trip.id)
        try {
            await respondCheckerCompanion(trip.id, "declined")
        } catch (requestError) {
            showToast(requestError?.message || "Couldn't decline that invite")
        } finally {
            setRespondingTripId(null)
            // A declined invite no longer belongs in this list at all.
            await fetchTrips(true)
        }
    }

    const handlePing = async (trip) => {
        try {
            await pingCheckerTrip(trip.id)
            showToast(
                `Safety ping sent to ${
                    [trip.owner_first_name, trip.owner_last_name]
                        .filter(Boolean)
                        .join(" ") ||
                    trip.owner_username ||
                    "the owner"
                }`,
            )
        } catch (requestError) {
            showToast(requestError?.message || "Couldn't send that ping")
        }
    }

    // Read/acknowledge only in V1 -- this remains a lightweight,
    // non-authoritative gesture (a toast, not a real dispatch), same as the
    // Android app.
    const handleEscalate = () => {
        showToast("Concern escalated to community chiefs")
    }

    const handleConfirmStopWatching = async () => {
        if (!leavingTrip) return

        setIsLeaving(true)
        try {
            await leaveCheckerTrip(leavingTrip.id)
            showToast("You've stopped watching this trip")
            setLeavingTrip(null)
            await fetchTrips(true)
        } catch (requestError) {
            showToast(requestError?.message || "Couldn't stop watching that trip")
        } finally {
            setIsLeaving(false)
        }
    }

    return (
        <div className="checker-page">
            <Helmet>
                <title>Chaperon - Convoy</title>
            </Helmet>

            <BreadCrumbs crumbs={crumbs} />
            <ToastNoPlace />

            <div className="container-fluid checker-container py-3">
                <div className="checker-header mb-4">
                    <h1 className="checker-title">Convoys</h1>
                    <p className="checker-subtitle mb-0">
                        Watch over Companions who have invited you to their
                        Checker trips.
                    </p>
                </div>

                <div className="checker-layout">
                    <div className="checker-main">
                        {error && <div className="alert alert-danger">{error}</div>}

                        {isLoading && trips.length === 0 ? (
                            <p className="text-muted">Loading convoy trips...</p>
                        ) : trips.length === 0 ? (
                            <div className="checker-empty-state">
                                <p>
                                    You&apos;re not currently watching anyone. <br /><br />
                                    When someone invites you as a Checker Companion and you
                                    accept, their active trip shows up here.
                                </p>
                                <img src={CONVOY_EMPTY_STATE_IMAGE_URL} alt="" />
                            </div>
                        ) : (
                            <div className="checker-trip-list">
                                {trips.map((trip) => (
                                    <ConvoyTripCard
                                        key={trip.id}
                                        trip={trip}
                                        isResponding={respondingTripId === trip.id}
                                        onAccept={() => handleAccept(trip)}
                                        onDecline={() => handleDecline(trip)}
                                        onPing={() => handlePing(trip)}
                                        onEscalate={handleEscalate}
                                        onStopWatching={() => setLeavingTrip(trip)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    <CommunityActivityColumn />
                </div>
            </div>

            <Modal
                open={!!leavingTrip}
                onClose={() => {
                    if (!isLeaving) setLeavingTrip(null)
                }}
                center
                classNames={{ modal: "checker-modal" }}
            >
                <h4 className="mb-3">
                    <i className="bi bi-exclamation-triangle text-warning pe-2"></i>
                    Stop watching this trip?
                </h4>
                <p>
                    You&apos;ll no longer be notified about &quot;
                    {leavingTrip?.title}&quot; and{" "}
                    {[leavingTrip?.owner_first_name, leavingTrip?.owner_last_name]
                        .filter(Boolean)
                        .join(" ") ||
                        leavingTrip?.owner_username ||
                        "the owner"}{" "}
                    won&apos;t be able to count on you as a safety companion.
                    This can&apos;t be undone.
                </p>
                <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleConfirmStopWatching}
                        disabled={isLeaving}
                    >
                        {isLeaving ? "Working..." : "Stop Watching"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setLeavingTrip(null)}
                        disabled={isLeaving}
                    >
                        Never mind
                    </button>
                </div>
            </Modal>

            <CommunitySidebar manager={communityManager} />
        </div>
    )
}
